import { defineComponent as _defineComponent } from 'vue'
import { ContactService, toastServiceError } from '@/services';
import { useContactTimelineFilterStore, useFilterStore, useUiStore } from '@/stores';
import { CallModel, ContactModel, ContactTimelineStats, DataItem, Permission } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { BookOpenIcon, MailIcon, PhoneIcon } from '@heroicons/vue/solid';
import { computed, onMounted, ref, Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
    ContactAuraPanel,
    ContactHealthScoreChart,
    ContactTimelineCallDetails,
    ContactTimelineFilters,
} from '@/views/pages/contact/components';
import moment from 'moment';
import { ContactTimelineModel } from '@/types/models/ContactTimelineModel';
import { formatSecToTime } from '@/utils/datetime';
import { useI18n } from 'vue-i18n';
import { timelineAnalysisMock } from './mocks/TimelineAnalysis';

const showAskAuraButton = false;

const perPage = 20;

export default /*@__PURE__*/_defineComponent({
  __name: 'ContactTimelinePage',
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const uiStore = useUiStore();
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);
const { t } = useI18n();

const contact: Ref<ContactModel | undefined> = ref(undefined);
const timelineStats: Ref<ContactTimelineStats | undefined> = ref(undefined);
const calls: Ref<CallModel[]> = ref([]);
const count = ref(0);
const currentPage = ref(1);

const filterStore = useFilterStore();
const timelineStore = useContactTimelineFilterStore();

const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (range) => filterStore.setDateRange(range),
});

const mainEmail = computed(() => contact.value?.emails.find((email) => email.isMain) ?? null);

const mainPhone = computed(() => contact.value?.phones.find((phone) => phone.isMain) ?? null);

const fetchContact = async (id: number) => {
    await setLoading(true);
    try {
        const response = await ContactService.get<ContactModel>(id);
        contact.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const fetchContactCalls = async (id: number) => {
    await setLoading(true);
    try {
        const response = await ContactService.getTimeline<ContactTimelineModel>(id, {
            params: {
                startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
                endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
                callType: timelineStore.callType?.value ?? null,
                sentiment: timelineStore.sentiment?.value ?? null,
                page: currentPage.value,
                per_page: perPage,
            },
        });
        calls.value = response.data.calls;
        timelineStats.value = response.data.stats;
        count.value = Number(response.headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const daysSinceLastContact = computed(() => {
    if (!timelineStats.value?.lastContactAt) return null;
    const lastContactDate = moment(timelineStats.value.lastContactAt);
    const today = moment();
    return today.diff(lastContactDate, 'days');
});

const healthScore = computed(() => contact.value?.healthScore ?? null);

const headerStats = computed<DataItem<string>[]>(() => {
    if (!timelineStats.value) return [];

    return [
        {
            name: 'totalCalls',
            value: timelineStats.value.totalCalls.toString(),
        },
        {
            name: 'averageDuration',
            value: formatSecToTime(timelineStats.value.averageDuration, true),
        },
        {
            name: 'positivePercentage',
            value:
                timelineStats.value.positivePercentage !== null
                    ? `${timelineStats.value.positivePercentage}%`
                    : '-',
        },
        {
            name: 'daysSinceLastContact',
            value: t('contact.timeline.stats.daysCount', { n: daysSinceLastContact.value ?? 0 }),
        },
    ];
});

const factors = timelineAnalysisMock.factors;
const healthScoreAnalysis = timelineAnalysisMock.analysis;

watch(
    [() => filterStore.dateRange, () => timelineStore.callType, () => timelineStore.sentiment],
    () => {
        const contactId = Number(route.params.id);
        if (currentPage.value === 1) fetchContactCalls(contactId);
        else currentPage.value = 1;
    },
    { deep: true },
);

watch(currentPage, () => {
    const contactId = Number(route.params.id);
    if (!isNaN(contactId)) fetchContactCalls(contactId);
});

onMounted(() => {
    const contactId = Number(route.params.id);
    if (!isNaN(contactId)) {
        fetchContact(contactId);
        fetchContactCalls(contactId);
    }
});

const __returned__ = { showAskAuraButton, route, uiStore, setLoading, t, contact, timelineStats, calls, count, perPage, currentPage, filterStore, timelineStore, dateRange, mainEmail, mainPhone, fetchContact, fetchContactCalls, daysSinceLastContact, healthScore, headerStats, factors, healthScoreAnalysis, get Permission() { return Permission }, get applyPhoneMask() { return applyPhoneMask }, get BookOpenIcon() { return BookOpenIcon }, get MailIcon() { return MailIcon }, get PhoneIcon() { return PhoneIcon }, get ContactAuraPanel() { return ContactAuraPanel }, get ContactHealthScoreChart() { return ContactHealthScoreChart }, get ContactTimelineCallDetails() { return ContactTimelineCallDetails }, get ContactTimelineFilters() { return ContactTimelineFilters } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})